import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { cityService } from '../../services/cityService';
import { countryService } from '../../services/countryService';
import { City, CreateCityRequest, UpdateCityRequest, Country } from '../../types/client';

interface Props {
  city?: City | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CityModal: React.FC<Props> = ({ city, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!city;

  const { data: countriesData } = useQuery({
    queryKey: ['countries', 'for-dropdown'],
    queryFn: () => countryService.getCountries({ page: 1, limit: 1000 }),
  });

  const countries = countriesData?.countries || [] as Country[];

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateCityRequest>({
    defaultValues: {
      name: city?.name || '',
      countryId: city?.countryId || '',
      status: city?.status || 'ACTIVE',
    }
  });

  useEffect(() => {
    if (city) {
      reset({ name: city.name, countryId: city.countryId, status: city.status });
    }
  }, [city, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateCityRequest) => cityService.createCity(data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to create city'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCityRequest) => cityService.updateCity(city!.id, data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to update city'),
  });

  const onSubmit = (data: CreateCityRequest) => {
    setError(null);
    isEditing ? updateMutation.mutate(data) : createMutation.mutate(data);
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit City' : 'Create City'}</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                {error && (<div className="mb-4 rounded-lg bg-red-50 p-4"><div className="text-sm text-red-700">{error}</div></div>)}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="form-label">City *</label>
                      <input {...register('name', { required: 'City is required', maxLength: { value: 100, message: 'Max 100 chars' } })} type="text" className="form-input" placeholder="Enter city name" />
                      {errors.name && <p className="form-error">{errors.name.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">Country *</label>
                      <select {...register('countryId', { required: 'Country is required' })} className="form-input">
                        <option value="">Select Country</option>
                        {countries.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      {errors.countryId && <p className="form-error">{errors.countryId.message as string}</p>}
                    </div>
                    <div>
                      <label className="form-label">Status</label>
                      <select {...register('status')} className="form-input">
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button type="submit" loading={isLoading}>{isEditing ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CityModal;

